import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const { user } = useAuth();

  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'Developer'
  });
  const [addUserLoading, setAddUserLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'Admin') {
      fetchUsers();
    }
  }, [user]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(user => 
        statusFilter === 'active' ? user.is_active : !user.is_active
      );
    }

    setFilteredUsers(filtered);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`/users/${userId}/role`, { role: newRole });
      fetchUsers();
    } catch (error) {
      alert('Failed to update user role');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserLoading(true);

    try {
      await axios.post('/register', newUser);
      setShowAddUser(false);
      setNewUser({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'Developer'
      });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (user?.role !== 'Admin') {
    return (
      <div className="main-content">
        <div className="alert alert-danger">
          <h3>🚫 Access Denied</h3>
          <p><strong>Admin privileges required.</strong></p>
          <p>Your current role: <code>{user?.role || 'Unknown'}</code></p>
          <p>You need Admin role to access User Management.</p>
          <Link to="/dashboard" className="btn btn-primary">
            Go to Your Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="main-content">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content user-management">
      <div className="page-header">
        <div className="header-content">
          <h1>👥 User Management</h1>
          <p>Manage system users, roles, and permissions</p>
        </div>
        <button 
          onClick={() => setShowAddUser(true)}
          className="btn btn-primary header-btn"
        >
          <span>➕</span> Add New User
        </button>
      </div>

      <div className="user-stats-grid">
        <div className="stat-card total-users">
          <div className="stat-icon">👤</div>
          <div className="stat-info">
            <div className="stat-value">{users.length}</div>
            <div className="stat-label">Total Users</div>
          </div>
        </div>
        <div className="stat-card active-users">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <div className="stat-value">{users.filter(u => u.is_active).length}</div>
            <div className="stat-label">Active Users</div>
          </div>
        </div>
        <div className="stat-card admin-count">
          <div className="stat-icon">🛡️</div>
          <div className="stat-info">
            <div className="stat-value">{users.filter(u => u.role === 'Admin').length}</div>
            <div className="stat-label">Administrators</div>
          </div>
        </div>
        <div className="stat-card recent-users">
          <div className="stat-icon">🆕</div>
          <div className="stat-info">
            <div className="stat-value">
              {users.filter(u => 
                new Date(u.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
              ).length}
            </div>
            <div className="stat-label">This Week</div>
          </div>
        </div>
      </div>

      <div className="filters-card">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Search Users</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, username, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Role</label>
            <select
              className="form-control form-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="Admin">Admin</option>
              <option value="Developer">Developer</option>
              <option value="Tester">Tester</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="form-control form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Actions</label>
            <button 
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('');
                setStatusFilter('');
              }}
              className="btn btn-outline w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="users-container">
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        <div className="users-grid">
          {filteredUsers.map(user => (
            <div key={user.id} className="user-card">
              <div className="user-card-header">
                <div className="user-avatar-large">
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </div>
                <div className="user-basic-info">
                  <h3 className="user-name">{user.first_name} {user.last_name}</h3>
                  <p className="user-username">@{user.username}</p>
                  <p className="user-email">{user.email}</p>
                </div>
                <div className="user-status-badge">
                  <span className={`status-indicator ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? '🟢 Active' : '🔴 Inactive'}
                  </span>
                </div>
              </div>

              <div className="user-card-body">
                <div className="user-role-section">
                  <label className="form-label">Role</label>
                  <select
                    className={`form-control form-select role-select role-${user.role.toLowerCase()}`}
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                  >
                    <option value="Admin">🛡️ Admin</option>
                    <option value="Developer">💻 Developer</option>
                    <option value="Tester">🧪 Tester</option>
                  </select>
                </div>

                <div className="user-meta">
                  <p className="join-date">Joined {formatDate(user.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredUsers.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No users found</h3>
            <p>Try adjusting your search criteria or add a new user.</p>
          </div>
        )}
      </div>

      {showAddUser && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3 className="modal-title">➕ Add New User</h3>
              <button
                onClick={() => setShowAddUser(false)}
                className="btn btn-outline btn-sm"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newUser.first_name}
                      onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newUser.last_name}
                      onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select
                      className="form-control form-select"
                      value={newUser.role}
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    >
                      <option value="Developer">💻 Developer</option>
                      <option value="Tester">🧪 Tester</option>
                      <option value="Admin">🛡️ Admin</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={addUserLoading}
                >
                  {addUserLoading ? (
                    <>
                      <div className="spinner"></div>
                      Adding User...
                    </>
                  ) : (
                    '➕ Add User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 